package com.htmlagent.site.i18n

import com.htmlagent.shared.UiLocale
import kotlinx.browser.document
import kotlinx.browser.localStorage
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import org.w3c.dom.get
import org.w3c.dom.set

/**
 * The language the interface is in.
 *
 * Same shape as [com.htmlagent.site.state.LayoutPrefs]: what the person chose wins, Spanish
 * decides when they have not chosen, and reads go through `runCatching` because
 * `localStorage` throws in a private window.
 *
 * The default is fixed and not read from the browser because `kobweb export` prerenders the
 * pages in a headless Chrome whose `navigator.language` is `en-US`: detecting it here wrote an
 * English page with `lang="en"` into the HTML that ships and gets indexed. Googlebot renders
 * with that same locale, so it never saw the Spanish either.
 *
 * It is a [StateFlow] and not a plain value because the presenter, which is not a composable
 * and cannot read a `CompositionLocal`, needs the current language too.
 */
object AppLocale {

    private const val KEY = "html-agent.locale"

    private val _current = MutableStateFlow(stored() ?: UiLocale.SPANISH)

    val current: StateFlow<UiLocale> = _current.asStateFlow()

    /** The texts for the language in force, for whoever is not a composable. */
    val strings: Strings get() = stringsFor(_current.value)

    fun stringsFor(locale: UiLocale): Strings =
        if (locale == UiLocale.SPANISH) SpanishStrings else EnglishStrings

    /**
     * Changes the language and remembers it. It also moves the document's `lang`, which is not
     * cosmetic: it is what makes a screen reader pronounce the text in the right language.
     */
    fun set(locale: UiLocale) {
        _current.value = locale
        applyLang(locale)
        runCatching { localStorage[KEY] = locale.tag }
    }

    /** Puts the `lang` in place on start-up, before anything is painted. */
    fun applyLang(locale: UiLocale = _current.value) {
        runCatching { document.documentElement?.setAttribute("lang", locale.tag) }
    }

    private fun stored(): UiLocale? =
        runCatching { localStorage[KEY] }.getOrNull()?.let { UiLocale.ofTag(it) }
}
